import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, Pencil, Trash2, Check, X, Loader2, CircleDot, GripVertical } from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'
import {
  useCircles,
  useCreateCircle,
  useUpdateCircle,
  useDeleteCircle,
  useReorderCircles,
} from '@/hooks/use-circles'
import type { Circle } from '@/lib/types'

const PRESET_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#DCCBF5', // lilac
  '#ec4899', // pink
  '#6b7280', // gray
]

function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PRESET_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          className={cn(
            'h-7 w-7 rounded-full border-2 transition-transform',
            value === color ? 'border-foreground scale-110' : 'border-transparent',
          )}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  )
}

function SortableCircleRow({
  circle,
  editingId,
  editName,
  editColor,
  setEditName,
  setEditColor,
  deletingId,
  onStartEdit,
  onUpdate,
  onCancelEdit,
  onStartDelete,
  onConfirmDelete,
  onCancelDelete,
  onNavigate,
  updatePending,
  deletePending,
  t,
}: {
  circle: Circle
  editingId: string | null
  editName: string
  editColor: string
  setEditName: (v: string) => void
  setEditColor: (v: string) => void
  deletingId: string | null
  onStartEdit: (circle: Circle) => void
  onUpdate: () => void
  onCancelEdit: () => void
  onStartDelete: (id: string) => void
  onConfirmDelete: (id: string) => void
  onCancelDelete: () => void
  onNavigate: (circleId: string) => void
  updatePending: boolean
  deletePending: boolean
  t: TFunction
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: circle.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'z-10 opacity-80 shadow-lg')}>
      {editingId === circle.id ? (
        /* Inline edit form */
        <div className="space-y-3 p-4">
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            onKeyDown={(e) => {
              if (e.key === 'Enter') onUpdate()
              if (e.key === 'Escape') onCancelEdit()
            }}
            autoFocus
          />
          <ColorPicker value={editColor} onChange={setEditColor} />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onUpdate}
              disabled={!editName.trim() || updatePending}
              className="bg-primary text-primary-foreground flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {updatePending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {t('common.save', 'Save')}
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              className="border-outline-variant/30 hover:bg-surface-container-high flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium"
            >
              <X className="h-3.5 w-3.5" />
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      ) : deletingId === circle.id ? (
        /* Delete confirm */
        <div className="flex items-center justify-between p-4">
          <p className="text-on-surface-variant text-sm">
            {(circle.contactCount ?? 0) > 0
              ? t(
                  'circles.confirmDeleteWithContacts',
                  'Delete "{{name}}"? {{count}} contact(s) will be unlinked.',
                  { name: circle.name, count: circle.contactCount ?? 0 },
                )
              : t('circles.confirmDelete', 'Delete "{{name}}"?', { name: circle.name })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onConfirmDelete(circle.id)}
              disabled={deletePending}
              className="bg-destructive text-destructive-foreground flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {deletePending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('common.delete', 'Delete')}
            </button>
            <button
              type="button"
              onClick={onCancelDelete}
              className="border-outline-variant/30 hover:bg-surface-container-high rounded-lg border px-3 py-1.5 text-sm font-medium"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      ) : (
        /* Normal row */
        <div className="flex items-center gap-3 p-4">
          <button
            type="button"
            className="text-on-surface-variant flex h-8 w-8 shrink-0 cursor-grab touch-none items-center justify-center active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onNavigate(circle.id)}
            className="flex flex-1 items-center gap-3"
          >
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full"
              style={{ backgroundColor: circle.color ?? '#6b7280' }}
            />
            <span className="flex-1 text-left text-sm font-medium">{circle.name}</span>
            <span className="text-on-surface-variant text-xs">{circle.contactCount ?? 0}</span>
          </button>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onStartEdit(circle)}
              className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
            >
              <Pencil className="text-on-surface-variant h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onStartDelete(circle.id)}
              className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
            >
              <Trash2 className="text-on-surface-variant h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

export default function CirclesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: circles, isLoading } = useCircles()
  const createCircle = useCreateCircle()
  const updateCircle = useUpdateCircle()
  const deleteCircle = useDeleteCircle()
  const reorderCircles = useReorderCircles()

  const [showAddForm, setShowAddForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(PRESET_COLORS[5])

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')

  const [deletingId, setDeletingId] = useState<string | null>(null)

  // Local order state for optimistic reorder
  const [localOrder, setLocalOrder] = useState<Circle[] | null>(null)
  const displayCircles = localOrder ?? circles ?? []

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleCreate() {
    const name = newName.trim()
    if (!name) return

    createCircle.mutate(
      { name, color: newColor },
      {
        onSuccess: () => {
          setNewName('')
          setNewColor(PRESET_COLORS[5])
          setShowAddForm(false)
        },
      },
    )
  }

  function startEdit(circle: Circle) {
    setEditingId(circle.id)
    setEditName(circle.name)
    setEditColor(circle.color ?? PRESET_COLORS[5])
  }

  function handleUpdate() {
    if (!editingId) return
    const name = editName.trim()
    if (!name) return

    updateCircle.mutate(
      { id: editingId, name, color: editColor },
      {
        onSuccess: () => {
          setEditingId(null)
        },
      },
    )
  }

  function handleDelete(id: string) {
    deleteCircle.mutate(id, {
      onSuccess: () => {
        setDeletingId(null)
        setLocalOrder(null)
      },
    })
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id || !circles) return

    const oldIndex = displayCircles.findIndex((c) => c.id === active.id)
    const newIndex = displayCircles.findIndex((c) => c.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(displayCircles, oldIndex, newIndex)
    setLocalOrder(reordered)

    reorderCircles.mutate(
      reordered.map((c) => c.id),
      {
        onSettled: () => {
          setLocalOrder(null)
        },
      },
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="border-outline-variant/20 flex items-center justify-between border-b px-6 py-4">
        <h1 className="font-heading text-lg font-semibold">{t('circles.title', 'Circles')}</h1>
        <button
          type="button"
          onClick={() => setShowAddForm(!showAddForm)}
          className="hover:bg-surface-container-high flex h-8 w-8 items-center justify-center rounded-lg"
        >
          {showAddForm ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg">
          {/* Add form */}
          {showAddForm && (
            <div className="border-outline-variant/20 border-b p-4">
              <div className="space-y-3">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('circles.namePlaceholder', 'Circle name...')}
                  className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate()
                  }}
                  autoFocus
                />
                <ColorPicker value={newColor} onChange={setNewColor} />
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!newName.trim() || createCircle.isPending}
                  className="bg-primary text-primary-foreground flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {createCircle.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('circles.add', 'Add Circle')}
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-on-surface-variant h-6 w-6 animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && circles && circles.length === 0 && (
            <EmptyState
              icon={CircleDot}
              title={t('circles.empty', 'No circles yet')}
              description={t('circles.emptyHint', 'Create a circle to group your contacts.')}
              ctaLabel={t('circles.addCircle', 'Add Circle')}
              onCtaClick={() => setShowAddForm(true)}
            />
          )}

          {/* Circle list with drag-to-reorder */}
          {displayCircles.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={displayCircles.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="divide-outline-variant/20 divide-y">
                  {displayCircles.map((circle) => (
                    <SortableCircleRow
                      key={circle.id}
                      circle={circle}
                      editingId={editingId}
                      editName={editName}
                      editColor={editColor}
                      setEditName={setEditName}
                      setEditColor={setEditColor}
                      deletingId={deletingId}
                      onStartEdit={startEdit}
                      onUpdate={handleUpdate}
                      onCancelEdit={() => setEditingId(null)}
                      onStartDelete={setDeletingId}
                      onConfirmDelete={handleDelete}
                      onCancelDelete={() => setDeletingId(null)}
                      onNavigate={(id) => navigate(`/contacts?circle_id=${id}`)}
                      updatePending={updateCircle.isPending}
                      deletePending={deleteCircle.isPending}
                      t={t}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  )
}
